import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import L from '../common/localized';

const getSizeIncrement = (micro, smallest, smaller, bigger, biggest) => {
  if (biggest) return '1em';
  if (bigger) return '0.5em';
  if (smaller) return '-0.5em';
  if (smallest) return '-0.8em';
  if (micro) return '-1em';
  return '0em';
};

const UITitle = ({ as, micro, smallest, smaller, bigger, biggest, lines, style, ...rest }) => (
  <UITitleElement as={as} sizeIncrement={getSizeIncrement(micro, smallest, smaller, bigger, biggest)}
    {...rest}>
    {lines.map((line, index) => {
      if (!line) return null;
      return (
        /* eslint-disable-next-line react/no-array-index-key */
        <TitleLine key={index} {...rest} style={style}>
          <L text={line} />
        </TitleLine>
      );
    })}
  </UITitleElement>
);

if (process.env.NODE_ENV !== 'production') {
  UITitle.propTypes = {
    // It is important to specify which tag the title should be, for SEO purposes.
    as: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']).isRequired,
    // The content of the title is given as an array of lines.
    lines: PropTypes.arrayOf(PropTypes.string).isRequired,
    // The title has some margin by default, in order to be compatible by default with all other related UI components,
    // but in some cases, no margin is required, and so this is useful.
    noBottomMargin: PropTypes.bool,
    color: PropTypes.string,
    // These flags dictate the size of the title.
    smallest: PropTypes.bool,
    smaller: PropTypes.bool,
    bigger: PropTypes.bool,
    biggest: PropTypes.bool,
    micro: PropTypes.bool,
    underline: PropTypes.bool,
    underlineOffset: PropTypes.bool,
    fontWeight: PropTypes.number,
    // This flag ensures to use a de-emphasized style for the title.
    // This is appropriate for subtitle headings.
    deemph: PropTypes.bool,
    // This centers the textual content of a title.
    centered: PropTypes.bool,
    // This is in case that smaller, bigger and biggest props are not sufficient for sizing.
    regularSizes: PropTypes.objectOf(PropTypes.string),
    alignText: PropTypes.string,
    padding: PropTypes.string,
    lineHeight: PropTypes.string,
    noWrap: PropTypes.bool
  };
}

UITitle.defaultProps = {
  color: '#131313',
  smallest: false,
  smaller: false,
  bigger: false,
  biggest: false,
  deemph: false,
  centered: false,
  micro: false,
  alignText: 'left',
  regularSizes: {
    default: '2.1em',
    375: '2.5em',
    411: '2.6em',
    768: '2.2em',
    992: '3em',
    1200: '3.7em',
    1400: '4.2em',
    1600: '4.5em'
  },
  fontWeight: 700
};

const UITitleElement = styled.h1`
  ${props => props.centered &&
    'display: flex; justify-content: center; align-items: center; flex-direction: column'};
  ${props => !props.noBottomMargin && 'margin-bottom: 20px;'};
  ${props => props.noWrap && 'white-space: nowrap'};
  color: ${props => props.color};
  line-height: ${props => props.lineHeight ? props.lineHeight : '1.25'};
  width: 100%;
  padding: ${props => props.padding ? props.padding : '0'};
  text-decoration:  ${props => props.underline ? 'underline' : 'none'};
  text-underline-offset: ${props => props.underlineOffset ? '0.5rem' : '0'};
  font-size: ${props => `calc(${props.regularSizes.default} + ${props.sizeIncrement})`};
  @media screen and (min-width: 375px) {
    font-size: ${props => `calc(${props.regularSizes[375]} + ${props.sizeIncrement})`};
  }
  @media screen and (min-width: 411px) {
    font-size: ${props => `calc(${props.regularSizes[411]} + ${props.sizeIncrement})`};
  }
  @media screen and (min-width: 768px) {
    font-size: ${props => `calc(${props.regularSizes[768]} + ${props.sizeIncrement})`};
  }
  @media screen and (min-width: 992px) {
    font-size: ${props => `calc(${props.regularSizes[992]} + ${props.sizeIncrement})`};
  }
  @media screen and (min-width: 1200px) {
    font-size: ${props => `calc(${props.regularSizes[1200]} + ${props.sizeIncrement})`};
  }
  @media screen and (min-width: 1400px) {
    font-size: ${props => `calc(${props.regularSizes[1400]} + ${props.sizeIncrement})`};
  }
  @media screen and (min-width: 1600px) {
    font-size: ${props => `calc(${props.regularSizes[1600]} + ${props.sizeIncrement})`};
  }
`;

const TitleLine = styled.span`
  display: block;
  font-weight: ${props => props.fontWeight};
  letter-spacing: ${props => props.fontWeight === 900 ? '-0.03em' : '0'};
  word-spacing: ${props => props.fontWeight === 900 ? '0.1em' : '0'};
  word-break: break-word;
  ${props => props.deemph ? 'font-weight: 300; font-size: 0.7em;' : ''};
  ${props => props.centered && 'text-align: center;'};
  text-align: ${props => props.alignText};
`;

export default UITitle;
